import React, { useState, useEffect } from 'react';
import logoImage from '../assets/logo.png';
import cachet from '../assets/cachet.png';
import { formatLibelleForDisplay } from '../utils/libelleFormatter';
import FondClientDetailsModal from './FondClientDetailsModal';
import './EtatInventory.css';
import './NHPage.css';
import { authJsonHeaders } from '../utils/authHeaders';
import { shouldShowCaisseCarryLine } from '../utils/caisseActiveYear';

/** Appels API avec token + cabinet actif (crédits suivent le client déplacé). */
function apiFetch(url, options = {}) {
    return fetch(url, {
        ...options,
        headers: {
            ...authJsonHeaders(),
            ...(options.headers || {}),
        },
    });
}
// Fonction helper pour vérifier si un libellé est un honoraire reçu (incluant les deux types)
const isHonoraireRecu = (libelle) => {
    if (!libelle) return false;
    const libelleUpper = libelle.toUpperCase();
    return libelleUpper.includes('HONORAIRES REÇU') ||
        libelleUpper.includes('HONORAIRES RECU') ||
        libelleUpper.includes('HONORAIRES RE') ||
        libelleUpper.includes('HONORAIRES PLATEFORME TEJ') ||
        libelleUpper === 'HONORAIRES PLATEFORME TEJ';
};

export default function EtatCgmModal({ show, onClose, filteredData = null, type = null, filterType = null, date = null, mois = null, annee = null, beneficiaire = null, dateDebut = null, dateFin = null, fondClientView = 'etat' }) {
    const [honoraires, setHonoraires] = useState([]);
    const [depenses, setDepenses] = useState([]);
    const [chargesPayeesParFondClient, setChargesPayeesParFondClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filterInfo, setFilterInfo] = useState(null);
    const [beneficiaireData, setBeneficiaireData] = useState(null);
    const [clientName, setClientName] = useState('');
    const [soldeReporte, setSoldeReporte] = useState(null);
    const [soldeCaisseCgm, setSoldeCaisseCgm] = useState(0);
    const [soldeDebutJournee, setSoldeDebutJournee] = useState(0);
    const [fondClientDetails, setFondClientDetails] = useState(null);
    const [fondClientSearch, setFondClientSearch] = useState('');

    useEffect(() => {
        if (show) {
            setHonoraires([]);
            setDepenses([]);
            setChargesPayeesParFondClient([]);
            setFondClientSearch('');
            setError('');
            setFilterInfo(null);
            setBeneficiaireData(null);
            setClientName('');
            setSoldeReporte(null);
            setSoldeCaisseCgm(0);
            setSoldeDebutJournee(0);
            setSoldeDebutJournee(0);

            // Réinitialiser les variables globales si c'est un état bureau
            const etatType = type || window.currentEtatType;
            if (etatType === 'bureau') {
                window.currentEtatClientId = null;
                window.currentEtatType = 'bureau';
            }

            // Récupérer le nom du client si on est dans un contexte client
            const currentClientId = window.currentEtatClientId || window.currentHonorairesClientId;
            if (currentClientId) {
                fetchClientName(currentClientId);
                fetchSoldeReporte(currentClientId);
            }

            if (filteredData) {
                // Utiliser les données filtrées normales
                setHonoraires(filteredData.honoraires || []);
                setDepenses(filteredData.depenses || []);
                setFilterInfo({
                    type: filteredData.filterType,
                    value: filteredData.filterValue,
                    clientName: filteredData.clientName || null
                });
                // Récupérer le solde de la caisse CGM pour l'état bureau filtré par jour, mois, période ou année
                const etatType = window.currentEtatType || type;
                const filterTypeForCaisse = filteredData.filterType || filterType;
                if (etatType === 'bureau' && (
                    filterTypeForCaisse === 'jour' ||
                    filterTypeForCaisse === 'mois' ||
                    filterTypeForCaisse === 'periode' ||
                    filterTypeForCaisse === 'annee'
                )) {
                    fetchSoldeCaisseCgm();
                }
            } else if (filterType === 'beneficiaire' && beneficiaire) {
                // État par bénéficiaire spécifique
                setLoading(true);
                fetchBeneficiaireData(beneficiaire)
                    .finally(() => setLoading(false));
            } else {
                // Charger toutes les données selon le type
                setLoading(true);
                const etatType = window.currentEtatType || type;
                if (etatType === 'bureau') {
                    // Pour l'état CGM (bureau), charger seulement les honoraires et les dépenses bureau
                    // Récupérer le solde de la caisse CGM pour les filtres par jour, mois, période ou année
                    const promises = [fetchAllHonoraires(), fetchAllDepenses()];
                    if (filterType === 'jour' || filterType === 'mois' || filterType === 'periode' || filterType === 'annee') {
                        promises.push(fetchSoldeCaisseCgm());
                    }
                    Promise.all(promises)
                        .finally(() => setLoading(false));
                } else if (etatType === 'fond-client-cgm') {
                    // Pour fond client chez CGM, charger les charges argent client et les charges payées par fond client
                    Promise.all([fetchAllHonoraires(), fetchChargesPayeesParFondClient()])
                        .finally(() => setLoading(false));
                } else {
                    // Pour l'état client, charger les honoraires et les dépenses client
                    Promise.all([fetchAllHonoraires(), fetchAllDepenses()])
                        .finally(() => setLoading(false));
                }
            }
        }
    }, [show, filteredData, type, filterType, date, mois, annee, beneficiaire, dateDebut, dateFin]);

    // Écouter les événements de mise à jour de charge pour rafraîchir l'état client
    useEffect(() => {
        const handleChargeUpdated = () => {
            // Vérifier si c'est un état client et que le modal est ouvert
            const etatType = window.currentEtatType || type;
            if (show && etatType === 'client') {
                // Recharger les données pour l'état client
                setLoading(true);
                Promise.all([fetchAllHonoraires(), fetchAllDepenses()])
                    .finally(() => setLoading(false));
            }
        };

        const handleEtatClientUpdated = (event) => {
            // Vérifier si c'est pour le même client
            const currentClientId = window.currentEtatClientId || window.currentHonorairesClientId;
            if (show && event.detail && event.detail.clientId == currentClientId) {
                // Recharger les données pour l'état client
                setLoading(true);
                Promise.all([fetchAllHonoraires(), fetchAllDepenses()])
                    .finally(() => setLoading(false));
            }
        };

        window.addEventListener('charge-updated', handleChargeUpdated);
        window.addEventListener('etat-client-updated', handleEtatClientUpdated);

        return () => {
            window.removeEventListener('charge-updated', handleChargeUpdated);
            window.removeEventListener('etat-client-updated', handleEtatClientUpdated);
        };
    }, [show, type]);

    const fetchClientName = async (clientId) => {
        try {
            const response = await apiFetch(`/api/clients/${clientId}`);
            const data = await response.json();

            if (data.success && data.client) {
                const fullName = `${data.client.nom || ''} ${data.client.prenom || ''}`.trim();
                setClientName(fullName);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du nom du client:', error);
        }
    };

    const fetchSoldeReporte = async (clientId) => {
        try {
            const currentYear = new Date().getFullYear();
            const response = await apiFetch(`/api/charges-mensuelles/solde-reporte/${clientId}?annee=${currentYear}`);
            const data = await response.json();
            if (data.success) {
                setSoldeReporte(data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du solde reporté:', error);
        }
    };

    const fetchSoldeCaisseCgm = async () => {
        try {
            const etatType = window.currentEtatType || type;
            const currentFilterType = filterInfo?.type || filterType;

            // Déterminer la date à utiliser pour le solde "de début"
            // - Filtre par jour      → début de cette journée (fin de la veille)
            // - Filtre par mois      → début du 1er jour du mois sélectionné (fin du mois précédent)
            let dateForSoldeDebut = null;
            if (etatType === 'bureau') {
                if (currentFilterType === 'jour' && (date || filterInfo?.value)) {
                    dateForSoldeDebut = date || filterInfo?.value;
                } else if (currentFilterType === 'mois') {
                    const currentMois = mois || filterInfo?.mois;
                    const currentAnnee = annee || filterInfo?.annee;
                    if (currentMois && currentAnnee) {
                        const m = String(currentMois).padStart(2, '0');
                        dateForSoldeDebut = `${currentAnnee}-${m}-01`;
                    }
                } else if (currentFilterType === 'annee') {
                    const currentAnnee = annee || filterInfo?.annee || filterInfo?.value;
                    if (currentAnnee) {
                        dateForSoldeDebut = `${currentAnnee}-01-01`;
                    }
                } else if (currentFilterType === 'periode') {
                    const currentDebut = dateDebut || filterInfo?.dateDebut || (typeof filterInfo?.value === 'string' && filterInfo.value.includes(' - ') ? filterInfo.value.split(' - ')[0] : filterInfo?.value);
                    if (currentDebut) {
                        dateForSoldeDebut = currentDebut;
                    }
                }
            }

            if (dateForSoldeDebut) {
                // Tous les filtres utilisent maintenant /reste-cumule pour calculer le solde d'ouverture
                // (cumul de tout ce qui est AVANT la date sélectionnée)
                console.log(`[Solde Debut] Appel API reste-cumule pour date: ${dateForSoldeDebut}`);
                try {
                    const soldeDebutResponse = await apiFetch(`/api/caisse-cgm/reste-cumule?date=${dateForSoldeDebut}`);
                    const soldeDebutData = await soldeDebutResponse.json();
                    console.log(`[Solde Debut] Réponse:`, soldeDebutData);
                    if (soldeDebutData.success) {
                        setSoldeDebutJournee(soldeDebutData.reste_cgm ?? 0);
                        console.log(`[Solde Debut] Défini à ${soldeDebutData.reste_cgm ?? 0}`);
                    } else {
                        console.error(`[Solde Debut] Erreur API:`, soldeDebutData.error);
                        setSoldeDebutJournee(0);
                    }
                } catch (err) {
                    console.error(`[Solde Debut] Exception:`, err);
                    setSoldeDebutJournee(0);
                }
            }

            // Récupérer le solde actuel
            const response = await apiFetch('/api/caisse-cgm/solde');
            const data = await response.json();
            if (data.success) {
                setSoldeCaisseCgm(data.solde_actuel || 0);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du solde de la caisse CGM:', error);
            setSoldeCaisseCgm(0);
            setSoldeDebutJournee(0);
        }
    };

    const fetchAllHonoraires = async () => {
        try {
            let url = '/api/honoraires';
            const params = new URLSearchParams();

            // Ajouter le type si spécifié
            const etatType = window.currentEtatType || type;
            if (etatType && etatType !== 'bureau') {
                params.append('type', etatType);
            }

            // Ajouter le client_id si on est dans un contexte client spécifique
            const currentClientId = window.currentEtatClientId;
            if (currentClientId) {
                params.append('client_id', currentClientId);
            }

            // Ajouter les filtres de date selon le filterType
            if (filterType === 'jour' && date) {
                params.append('date', date);
            } else if (filterType === 'periode' && dateDebut && dateFin) {
                params.append('date_debut', dateDebut);
                params.append('date_fin', dateFin);
            } else if (filterType === 'mois' && mois && annee) {
                params.append('mois', mois);
                params.append('annee', annee);
            } else if (filterType === 'annee' && annee) {
                params.append('annee', annee);
            }

            // Construire l'URL avec les paramètres
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await apiFetch(url);
            const data = await response.json();

            let honorairesData = [];
            if (data.success) {
                honorairesData = data.honoraires || [];

                // Filtrer par bénéficiaire si spécifié
                if (filterType === 'beneficiaire' && beneficiaire) {
                    honorairesData = honorairesData.filter(honoraire => {
                        const clientName = `${honoraire.client_nom} ${honoraire.client_prenom}`.trim();
                        return clientName.toLowerCase().includes(beneficiaire.toLowerCase());
                    });
                }

                // Filtrer pour ne garder que :
                // - Etat client : HONORAIRES REÇU + AVANCE
                // - Etat CGM (bureau) : uniquement HONORAIRES REÇU
                // - Fond client chez CGM : uniquement ARGENT CLIENT CHEZ CGM (ne pas charger depuis /api/honoraires)
                const etatType = window.currentEtatType || type;
                if (etatType !== 'fond-client-cgm') {
                    honorairesData = honorairesData.filter(honoraire => {
                        const libelle = (honoraire.libelle || '').toUpperCase();
                        const isHonBase = isHonoraireRecu(honoraire.libelle);
                        const isAvance = libelle.includes('AVANCE');
                        const isHonoraireRecuCheck = etatType === 'bureau' ? isHonBase : (isHonBase || isAvance);
                        const hasAmount = parseFloat(honoraire.avance || honoraire.montant || 0) > 0;

                        return isHonoraireRecuCheck && hasAmount;
                    });
                } else {
                    // Pour fond client chez CGM, ne pas utiliser les honoraires depuis /api/honoraires
                    honorairesData = [];
                }

                // Si le nom du client n'est pas encore défini dans filterInfo, essayons de le déduire
                try {
                    const firstWithClient = honorairesData && honorairesData.find && honorairesData.find(h => (h.client_nom || h.client_prenom));
                    if (firstWithClient) {
                        const inferredClientName = `${firstWithClient.client_nom || ''} ${firstWithClient.client_prenom || ''}`.trim();
                        if (inferredClientName) {
                            setFilterInfo(prev => ({ ...(prev || {}), clientName: prev?.clientName || inferredClientName }));
                        }
                    }
                } catch (_) { /* no-op */ }
            }

            // Si on est dans un contexte client spécifique ET que l'état est de type "bureau" (CGM),
            // récupérer aussi les charges pour les convertir en honoraires.
            // Pour l'état client, on ne le fait pas pour éviter les doublons,
            // car /api/honoraires retourne déjà les honoraires à partir de charges_mensuelles.
            // Pour fond client chez CGM, récupérer les charges de TOUS les clients.
            const etatTypeForCharges = window.currentEtatType || type;
            if (etatTypeForCharges === 'fond-client-cgm' || (currentClientId && etatTypeForCharges === 'bureau')) {
                try {
                    // Déterminer l'année pertinente selon le filtre sélectionné
                    let yearForCharges = new Date().getFullYear();
                    if (filterType === 'annee' && annee) {
                        yearForCharges = parseInt(annee);
                    } else if (filterType === 'mois' && annee) {
                        yearForCharges = parseInt(annee);
                    } else if (filterType === 'jour' && date) {
                        yearForCharges = new Date(date).getFullYear();
                    } else if (filterType === 'periode' && dateDebut) {
                        yearForCharges = new Date(dateDebut).getFullYear();
                    }

                    let chargesData = { success: false, charges: [] };

                    // Pour fond client chez CGM, utiliser l'endpoint optimisé
                    if (etatTypeForCharges === 'fond-client-cgm') {
                        // Construire les paramètres pour l'endpoint optimisé
                        let fondClientUrl = '/api/charges-mensuelles/fond-client-cgm?';
                        const fondParams = new URLSearchParams();

                        if (filterType === 'annee' && annee) {
                            fondParams.append('annee', annee);
                        } else if (filterType === 'mois' && mois && annee) {
                            fondParams.append('mois', mois);
                            fondParams.append('annee', annee);
                        } else if (filterType === 'jour' && date) {
                            fondParams.append('date', date);
                        } else if (filterType === 'periode' && dateDebut && dateFin) {
                            fondParams.append('dateDebut', dateDebut);
                            fondParams.append('dateFin', dateFin);
                        } else {
                            fondParams.append('annee', yearForCharges);
                        }

                        const fondClientResponse = await apiFetch(fondClientUrl + fondParams.toString());
                        chargesData = await fondClientResponse.json();
                    } else {
                        // Pour bureau, récupérer les charges du client spécifique
                        const chargesResponse = await apiFetch(`/api/charges-mensuelles/client/${currentClientId}?annee=${yearForCharges}`);
                        chargesData = await chargesResponse.json();
                    }

                    if (chargesData.success && chargesData.charges) {
                        // Helpers de filtrage par date (alignés avec fetchAllDepenses)
                        const sameDay = (d1, d2) => {
                            try {
                                const nd = new Date(d1);
                                const y = nd.getFullYear();
                                const m = (nd.getMonth() + 1).toString().padStart(2, '0');
                                const day = nd.getDate().toString().padStart(2, '0');
                                const rowDate = `${y}-${m}-${day}`;
                                return rowDate === d2;
                            } catch (_) { return false; }
                        };
                        const monthOf = (d1) => {
                            const nd = new Date(d1);
                            return { y: nd.getFullYear(), m: nd.getMonth() + 1 };
                        };

                        // Appliquer le filtre de date sur les charges
                        let filteredCharges = [...chargesData.charges];
                        if (filterType === 'jour' && date) {
                            filteredCharges = filteredCharges.filter(c => sameDay(c.date, date));
                        } else if (filterType === 'mois' && mois && annee) {
                            filteredCharges = filteredCharges.filter(c => {
                                const { y, m } = monthOf(c.date);
                                return y === parseInt(annee) && m === parseInt(mois);
                            });
                        } else if (filterType === 'annee' && annee) {
                            filteredCharges = filteredCharges.filter(c => {
                                const nd = new Date(c.date);
                                return nd.getFullYear() === parseInt(annee);
                            });
                        } else if (filterType === 'periode' && dateDebut && dateFin) {
                            const start = new Date(dateDebut); start.setHours(0, 0, 0, 0);
                            const end = new Date(dateFin); end.setHours(23, 59, 59, 999);
                            filteredCharges = filteredCharges.filter(c => {
                                const nd = new Date(c.date);
                                return nd >= start && nd <= end;
                            });
                        }
                        
                        // Convertir les charges en honoraires pour l'affichage
                        // Pour l'état client : inclure HONORAIRES REÇU + AVANCE
                        // Pour l'état CGM : inclure seulement HONORAIRES REÇU
                        // Pour fond client chez CGM : inclure seulement ARGENT CLIENT CHEZ CGM
                        const etatType = window.currentEtatType || type;
                        const chargesAsHonoraires = filteredCharges
                            .filter(charge => {
                                const chargeLibelle = (charge.libelle || '').toUpperCase();

                                // Pour fond client chez CGM : l'endpoint backend filtre déjà par libellé,
                                // donc on accepte toutes les charges retournées (elles ont déjà le bon libellé)
                                if (etatType === 'fond-client-cgm') {
                                    // Vérifier seulement que l'avance est > 0 (le libellé est déjà filtré par le backend)
                                    return parseFloat(charge.avance || 0) > 0;
                                }

                                const isHonBase = isHonoraireRecu(charge.libelle);
                                const isAvance = chargeLibelle.includes('AVANCE');
                                // Pour état client : HONORAIRES REÇU ou AVANCE avec avance > 0
                                // Pour état CGM : seulement HONORAIRES REÇU avec avance > 0
                                const shouldInclude = etatType === 'bureau'
                                    ? (isHonBase && parseFloat(charge.avance || 0) > 0)
                                    : ((isHonBase || isAvance) && parseFloat(charge.avance || charge.montant || 0) > 0);
                                return shouldInclude;
                            })
                            .map(charge => {
                                // Préserver la précision des montants en utilisant les valeurs brutes
                                const avanceRaw = charge.avance || 0;
                                const montantRaw = charge.montant || 0;
                                // Utiliser le maximum entre avance et montant pour le montant affiché
                                const montantAffiche = Math.max(
                                    typeof avanceRaw === 'string' ? parseFloat(avanceRaw) : avanceRaw,
                                    typeof montantRaw === 'string' ? parseFloat(montantRaw) : montantRaw
                                );

                                // Pour fond client chez CGM, utiliser les informations du client depuis la charge
                                // Pour bureau, utiliser clientName
                                let clientNom = '';
                                let clientPrenom = '';
                                if (etatType === 'fond-client-cgm') {
                                    clientNom = charge.client_nom || '';
                                    clientPrenom = charge.client_prenom || '';
                                } else {
                                    clientNom = clientName.split(' ')[0] || '';
                                    clientPrenom = clientName.split(' ').slice(1).join(' ') || '';
                                }

                                return {
                                    id: `charge_${charge.id}`,
                                    date: charge.date,
                                    libelle: charge.libelle,
                                    montant: montantAffiche,
                                    avance: typeof avanceRaw === 'string' ? parseFloat(avanceRaw) : avanceRaw,
                                    client_id: charge.client_id,
                                    client_nom: clientNom,
                                    client_prenom: clientPrenom
                                };
                            });

                        // Pour fond client chez CGM, utiliser directement les charges converties
                        // Pour les autres types, combiner avec les honoraires déjà récupérés depuis /api/honoraires
                        if (etatType === 'fond-client-cgm') {
                            // Pour fond client chez CGM, utiliser uniquement les charges converties
                            honorairesData = chargesAsHonoraires.filter(honoraire => {
                                const amount = parseFloat(honoraire.avance || honoraire.montant || 0);
                                return amount > 0;
                            });
                        } else {
                            // Éviter les doublons en vérifiant l'ID
                            const existingIds = new Set(honorairesData.map(h => h.id));
                            const newHonoraires = chargesAsHonoraires.filter(h => !existingIds.has(h.id));
                            honorairesData = [...honorairesData, ...newHonoraires];

                            // Filtrer pour ne garder que les honoraires avec un montant > 0
                            honorairesData = honorairesData.filter(honoraire => {
                                const amount = parseFloat(honoraire.avance || honoraire.montant || 0);
                                return amount > 0;
                            });
                        }
                    }
                } catch (error) {
                    console.error('Erreur lors de la récupération des charges:', error);
                }
            }

            // Pour l'état CGM (bureau) : ajouter les dépôts de la caisse CGM (Caisse live)
            const etatTypeFinal = window.currentEtatType || type;
            if (etatTypeFinal === 'bureau') {
                try {
                    let depotsUrl = '/api/caisse-cgm/depots?';
                    const depotsParams = new URLSearchParams();
                    if (filterType === 'jour' && date) {
                        depotsParams.append('date', date);
                    } else if (filterType === 'periode' && dateDebut && dateFin) {
                        depotsParams.append('date_debut', dateDebut);
                        depotsParams.append('date_fin', dateFin);
                    } else if (filterType === 'mois' && mois && annee) {
                        depotsParams.append('mois', mois);
                        depotsParams.append('annee', annee);
                    } else if (filterType === 'annee' && annee) {
                        depotsParams.append('annee', annee);
                    } else {
                        // Par défaut : année en cours
                        depotsParams.append('annee', new Date().getFullYear());
                    }
                    depotsUrl += depotsParams.toString();
                    const depotsResponse = await apiFetch(depotsUrl);
                    const depotsJson = await depotsResponse.json();
                    if (depotsJson.success && Array.isArray(depotsJson.depots) && depotsJson.depots.length > 0) {
                        const depotsAsHonoraires = depotsJson.depots.map(d => ({
                            id: `depot_${d.id}`,
                            date: d.created_at,
                            libelle: (d.commentaire && d.commentaire.trim()) ? `Dépôt Caisse CGM - ${d.commentaire.trim()}` : 'Dépôt Caisse CGM',
                            client_nom: '',
                            client_prenom: '',
                            montant: parseFloat(d.montant || 0),
                            avance: 0
                        }));
                        honorairesData = [...honorairesData, ...depotsAsHonoraires];
                        honorairesData.sort((a, b) => new Date(a.date) - new Date(b.date));
                    }
                } catch (depotsErr) {
                    console.error('Erreur lors de la récupération des dépôts caisse CGM:', depotsErr);
                }
            }

            honorairesData.sort((a, b) => new Date(a.date) - new Date(b.date));
            setHonoraires(honorairesData);
            setError(''); // Réinitialiser l'erreur si tout s'est bien passé

            if (!data.success) {
                setError(data.error || 'Erreur lors du chargement des honoraires');
            }
        } catch (err) {
            console.error('Erreur dans fetchAllHonoraires:', err);
            setError('Erreur lors du chargement des honoraires');
            setHonoraires([]); // Réinitialiser les honoraires en cas d'erreur
        }
    };

    const fetchChargesPayeesParFondClient = async () => {
        try {
            const etatType = window.currentEtatType || type;
            if (etatType !== 'fond-client-cgm') {
                setChargesPayeesParFondClient([]);
                return;
            }

            // Construire les paramètres pour l'endpoint
            let url = '/api/charges-mensuelles/paye-par-fond-client?';
            const params = new URLSearchParams();

            if (filterType === 'annee' && annee) {
                params.append('annee', annee);
            } else if (filterType === 'mois' && mois && annee) {
                params.append('mois', mois);
                params.append('annee', annee);
            } else if (filterType === 'jour' && date) {
                params.append('date', date);
            } else if (filterType === 'periode' && dateDebut && dateFin) {
                params.append('dateDebut', dateDebut);
                params.append('dateFin', dateFin);
            } else {
                // Par défaut, utiliser l'année courante
                params.append('annee', new Date().getFullYear());
            }

            const response = await apiFetch(url + params.toString());
            const data = await response.json();

            if (data.success && data.charges) {
                // Convertir les charges en format similaire aux honoraires (inclure client_id pour le modal Détails)


                const chargesFormatees = data.charges.map(charge => ({
                    id: `charge_paye_${charge.id}`,
                    date: charge.date,
                    libelle: charge.libelle,
                    montant: parseFloat(charge.montant || 0),
                    avance: parseFloat(charge.avance || 0),
                    client_id: charge.client_id,
                    client_nom: charge.client_nom || '',
                    client_prenom: charge.client_prenom || ''
                }));
                setChargesPayeesParFondClient(chargesFormatees);
            } else {
                setChargesPayeesParFondClient([]);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des charges payées par fond client:', error);
            setChargesPayeesParFondClient([]);
        }
    };

    const fetchAllDepenses = async () => {
        try {
            // Pour fond client chez CGM, ne pas charger les dépenses
            const etatType = window.currentEtatType || type;
            if (etatType === 'fond-client-cgm') {
                setDepenses([]);
                return;
            }

            let url = '/api/depenses';
            const params = new URLSearchParams();

            // Ajouter le type si spécifié
            const isBureau = etatType === 'bureau';
            if (!isBureau && etatType) {
                params.append('type', etatType);
            }

            // Ajouter le client_id si on est dans un contexte client spécifique
            const currentClientId = window.currentEtatClientId;
            if (currentClientId) {
                params.append('client_id', currentClientId);
            }

            // Construire l'URL avec les paramètres selon le type (bureau ou client)
            if (isBureau) {
                // Pour l'état CGM (bureau), on doit récupérer :
                // 1. Toutes les dépenses bureau
                // 2. Les dépenses client avec préfixe [CGM] (payées par CGM)

                // D'abord, récupérer les dépenses bureau
                let bureauUrl = '/api/depenses/bureau';
                if (filterType === 'jour' && date) {
                    bureauUrl = '/api/depenses/bureau/par-periode';
                    const dateParams = new URLSearchParams();
                    dateParams.append('date_debut', date);
                    dateParams.append('date_fin', date);
                    bureauUrl += `?${dateParams.toString()}`;
                } else if (filterType === 'periode' && dateDebut && dateFin) {
                    bureauUrl = '/api/depenses/bureau/par-periode';
                    const dateParams = new URLSearchParams();
                    dateParams.append('date_debut', dateDebut);
                    dateParams.append('date_fin', dateFin);
                    bureauUrl += `?${dateParams.toString()}`;
                } else if (filterType === 'mois' && mois && annee) {
                    const start = `${annee}-${String(mois).padStart(2, '0')}-01`;
                    const endDate = new Date(annee, mois, 0);
                    const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                    bureauUrl = '/api/depenses/bureau/par-periode';
                    const dateParams = new URLSearchParams();
                    dateParams.append('date_debut', start);
                    dateParams.append('date_fin', end);
                    bureauUrl += `?${dateParams.toString()}`;
                } else if (filterType === 'annee' && annee) {
                    const start = `${annee}-01-01`;
                    const end = `${annee}-12-31`;
                    bureauUrl = '/api/depenses/bureau/par-periode';
                    const dateParams = new URLSearchParams();
                    dateParams.append('date_debut', start);
                    dateParams.append('date_fin', end);
                    bureauUrl += `?${dateParams.toString()}`;
                }

                // Ensuite, récupérer les dépenses client avec [CGM]
                let clientCgmUrl = '/api/depenses';
                const clientCgmParams = new URLSearchParams();
                if (filterType === 'jour' && date) {
                    clientCgmUrl = '/api/depenses/par-periode';
                    clientCgmParams.append('date_debut', date);
                    clientCgmParams.append('date_fin', date);
                } else if (filterType === 'periode' && dateDebut && dateFin) {
                    clientCgmUrl = '/api/depenses/par-periode';
                    clientCgmParams.append('date_debut', dateDebut);
                    clientCgmParams.append('date_fin', dateFin);
                } else if (filterType === 'mois' && mois && annee) {
                    const start = `${annee}-${String(mois).padStart(2, '0')}-01`;
                    const endDate = new Date(annee, mois, 0);
                    const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                    clientCgmUrl = '/api/depenses/par-periode';
                    clientCgmParams.append('date_debut', start);
                    clientCgmParams.append('date_fin', end);
                } else if (filterType === 'annee' && annee) {
                    const start = `${annee}-01-01`;
                    const end = `${annee}-12-31`;
                    clientCgmUrl = '/api/depenses/par-periode';
                    clientCgmParams.append('date_debut', start);
                    clientCgmParams.append('date_fin', end);
                }
                if (clientCgmParams.toString()) {
                    clientCgmUrl += `?${clientCgmParams.toString()}`;
                }

                // Récupérer les deux types de dépenses en parallèle
                console.log('🔍 Fetching dépenses bureau avec URL:', bureauUrl);
                console.log('🔍 Fetching dépenses client CGM avec URL:', clientCgmUrl);

                const [bureauResponse, clientCgmResponse] = await Promise.all([
                    apiFetch(bureauUrl),
                    apiFetch(clientCgmUrl)
                ]);

                const bureauData = await bureauResponse.json();
                const clientCgmData = await clientCgmResponse.json();

                console.log('📡 Réponse dépenses bureau:', bureauData);
                console.log('📡 Réponse dépenses client CGM:', clientCgmData);

                let depensesBureau = bureauData.success ? (bureauData.depenses || []) : [];
                let depensesClientCgm = clientCgmData.success ? (clientCgmData.depenses || []) : [];

                console.log('📊 Dépenses bureau brutes:', depensesBureau);
                console.log('📊 Dépenses client brutes:', depensesClientCgm);

                // Filtrer les dépenses client pour ne garder que celles avec [CGM]
                depensesClientCgm = depensesClientCgm.filter(dep => {
                    const desc = (dep.description || dep.libelle || '').toUpperCase();
                    const hasCgm = desc.includes('[CGM]');
                    if (hasCgm) {
                        console.log('✅ Dépense client CGM trouvée:', dep);
                    }
                    return hasCgm;
                });

                // Les dépenses bureau incluent déjà celles créées via "Payé par CGM" (elles sont dans beneficiaires_bureau)
                // Vérifier qu'elles sont bien là
                const depensesBureauAvecCgm = depensesBureau.filter(dep => {
                    const desc = (dep.description || dep.libelle || '').toUpperCase();
                    return desc.includes('[CGM]');
                });
                console.log('📊 Dépenses bureau avec [CGM]:', depensesBureauAvecCgm.length, depensesBureauAvecCgm);

                // Combiner les deux listes (marquer la source pour la suppression)
                let depensesData = [
                    ...depensesBureau.map(d => ({ ...d, _source: 'bureau' })),
                    ...depensesClientCgm.map(d => ({ ...d, _source: 'client' }))
                ];
                console.log('📊 Nombre de dépenses bureau:', depensesBureau.length);
                console.log('📊 Nombre de dépenses client CGM:', depensesClientCgm.length);
                console.log('📊 Nombre total de dépenses avant filtrage:', depensesData.length);

                // Pas besoin de filtrer par date car le backend l'a déjà fait
                // Mais on doit exclure les honoraires reçus
                depensesData = depensesData.filter(depense => {
                    const description = (depense.description || depense.libelle || '').toUpperCase();
                    const isHonoraireRecuCheck = isHonoraireRecu(description) ||
                        description.includes('HONORAIRES REÇU 60');
                    const isAvanceDeclaration = description.includes('AVANCE');
                    const shouldInclude = !isHonoraireRecuCheck && !isAvanceDeclaration;
                    if (!shouldInclude) {
                        console.log('❌ Dépense exclue (honoraire/avance):', depense);
                    }
                    return shouldInclude;
                });

                console.log('📦 Dépenses finales après filtrage honoraires:', depensesData.length);
                console.log('📦 Détail des dépenses finales:', depensesData);

                // Ajouter les retraits de la caisse CGM (affichés dans l'état et soustraits du Reste CGM)
                try {
                    let retraitsUrl = '/api/caisse-cgm/retraits?';
                    const retraitsParams = new URLSearchParams();
                    if (filterType === 'jour' && date) {
                        retraitsParams.append('date', date);
                    } else if (filterType === 'periode' && dateDebut && dateFin) {
                        retraitsParams.append('date_debut', dateDebut);
                        retraitsParams.append('date_fin', dateFin);
                    } else if (filterType === 'mois' && mois && annee) {
                        retraitsParams.append('mois', mois);
                        retraitsParams.append('annee', annee);
                    } else if (filterType === 'annee' && annee) {
                        retraitsParams.append('annee', annee);
                    } else {
                        retraitsParams.append('annee', new Date().getFullYear());
                    }
                    retraitsUrl += retraitsParams.toString();
                    const retraitsResponse = await apiFetch(retraitsUrl);
                    const retraitsJson = await retraitsResponse.json();
                    if (retraitsJson.success && Array.isArray(retraitsJson.retraits) && retraitsJson.retraits.length > 0) {
                        const toDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
                        const sameAmount = (a, b) => Math.abs(parseFloat(a || 0) - parseFloat(b || 0)) < 0.01;
                        const retraitsAsDepenses = retraitsJson.retraits
                            .filter(r => {
                                const isCashDrop =
                                    r.source_type === 'cash_drop' ||
                                    r.source_type === 'caisse_operation' ||
                                    r.type_retrait != null ||
                                    r.retrait_type != null;
                                const rDay = toDay(r.date || r.created_at);
                                const rMontant = parseFloat(r.montant || 0);
                                const rComment = (r.commentaire || '').trim();

                                // Inclure tous les retraits caisse (bureau + autre)
                                if (!isCashDrop && !rComment) return false;

                                const alreadyExists = depensesData.some(d => {
                                    if (String(d.id) === `retrait_caisse_${r.id}` || String(d.id) === `cash_drop_${r.id}`) {
                                        return true;
                                    }
                                    if (!sameAmount(d.montant, rMontant)) return false;
                                    if (toDay(d.date || d.date_operation) !== rDay) return false;
                                    const dLib = (d.libelle || d.description || '').trim();
                                    if (rComment && dLib && dLib === rComment) return true;
                                    // Legacy [CGM] liés BUREAU -
                                    if (rComment.toUpperCase().startsWith('BUREAU')) {
                                        const dLibU = dLib.toUpperCase();
                                        if (!dLibU.includes('[CGM]')) return false;
                                        const benef = (d.nom_beneficiaire || d.client || d.beneficiaire || '').trim().toUpperCase();
                                        const partAfterBureau = rComment.replace(/^BUREAU\s*-\s*/i, '').toUpperCase();
                                        return benef && partAfterBureau.includes(benef);
                                    }
                                    return false;
                                });
                                return !alreadyExists;
                            })
                            .map(r => {
                                const isBureau =
                                    String(r.type_retrait || '').toLowerCase() === 'bureau' ||
                                    String(r.retrait_type || '').toLowerCase() === 'bureau' ||
                                    Number(r.type_retrait) === 1 ||
                                    r.is_bureau;
                                const isInventaire =
                                    String(r.type_retrait || '').toLowerCase() === 'inventaire' ||
                                    String(r.retrait_type || '').toLowerCase() === 'inventaire';
                                const note = (r.commentaire && r.commentaire.trim())
                                    ? r.commentaire.trim()
                                    : (isBureau ? 'Retrait caisse bureau' : isInventaire ? 'Retrait inventaire' : 'Retrait caisse');
                                return {
                                    id: `retrait_caisse_${r.id}`,
                                    date: r.date || r.created_at,
                                    date_operation: r.date || r.created_at,
                                    description: note,
                                    libelle: note,
                                    montant: parseFloat(r.montant || 0),
                                    beneficiaire: isBureau ? 'Caisse CGM (Bureau)' : isInventaire ? 'Caisse CGM (Inventaire)' : 'Caisse CGM',
                                    nom_beneficiaire: isBureau ? 'Caisse CGM (Bureau)' : isInventaire ? 'Caisse CGM (Inventaire)' : 'Caisse CGM',
                                    client: isBureau ? 'Caisse CGM (Bureau)' : isInventaire ? 'Caisse CGM (Inventaire)' : 'Caisse CGM',
                                    _source: isBureau ? 'cash_drop_bureau' : isInventaire ? 'cash_drop_inventaire' : 'cash_drop_autre',
                                    type_retrait: isBureau ? 'bureau' : isInventaire ? 'inventaire' : 'autre',
                                };
                            });
                        depensesData = [...depensesData, ...retraitsAsDepenses];
                        depensesData.sort((a, b) => new Date(a.date || a.date_operation) - new Date(b.date || b.date_operation));
                    }
                } catch (retraitsErr) {
                    console.error('Erreur lors de la récupération des retraits caisse CGM:', retraitsErr);
                }

                setDepenses(depensesData);
                return; // Sortir de la fonction car on a déjà traité le cas bureau
            } else {
                // Pour les dépenses client, utiliser l'endpoint par période quand on a des filtres de date
                if (filterType === 'jour' && date) {
                    url = '/api/depenses/par-periode';
                    params.append('date_debut', date);
                    params.append('date_fin', date);
                    if (params.toString()) {
                        url += `?${params.toString()}`;
                    }
                } else if (filterType === 'periode' && dateDebut && dateFin) {
                    url = '/api/depenses/par-periode';
                    params.append('date_debut', dateDebut);
                    params.append('date_fin', dateFin);
                    if (params.toString()) {
                        url += `?${params.toString()}`;
                    }
                } else if (filterType === 'mois' && mois && annee) {
                    // Passer tout le mois au backend pour éviter les soucis de fuseau (Date parsing du navigateur)
                    const start = `${annee}-${String(mois).padStart(2, '0')}-01`;
                    const endDate = new Date(annee, mois, 0); // dernier jour du mois
                    const end = `${annee}-${String(mois).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                    url = '/api/depenses/par-periode';
                    params.append('date_debut', start);
                    params.append('date_fin', end);
                    if (params.toString()) {
                        url += `?${params.toString()}`;
                    }
                } else if (filterType === 'annee' && annee) {
                    const start = `${annee}-01-01`;
                    const end = `${annee}-12-31`;
                    url = '/api/depenses/par-periode';
                    params.append('date_debut', start);
                    params.append('date_fin', end);
                    if (params.toString()) {
                        url += `?${params.toString()}`;
                    }
                } else {
                    // Ajouter les params le cas échéant
                    if (params.toString()) {
                        url += `?${params.toString()}`;
                    }
                }
            }

            // Construire l'URL finale pour les dépenses client
            const finalUrl = url;
            console.log('🔍 Fetching dépenses client avec URL:', finalUrl);
            const response = await apiFetch(finalUrl);
            const data = await response.json();
            console.log('📡 Réponse dépenses client:', data);
            console.log('📊 Nombre de dépenses reçues:', data.depenses?.length || 0);

            if (data.success) {
                let depensesData = data.depenses || [];
                console.log('📦 Dépenses avant filtrage:', depensesData.length);
                console.log('📦 Total retourné par le backend:', data.total);

                // Filtrer par bénéficiaire si spécifié
                if (filterType === 'beneficiaire' && beneficiaire) {
                    depensesData = depensesData.filter(depense => {
                        return depense.beneficiaire &&
                            depense.beneficiaire.toLowerCase().includes(beneficiaire.toLowerCase());
                    });
                }

                // Inclure toutes les dépenses client (y compris celles payées par CGM)
                // Le libellé [CGM] sera remplacé par [PAYÉ PAR CGM] dans l'affichage

                console.log('📦 Dépenses finales après tous les filtres:', depensesData.length);
                console.log('📦 Dépenses détaillées:', depensesData);
                // Stocker aussi le total retourné par le backend pour référence
                setDepenses(depensesData);
                // Stocker le total du backend dans une variable pour utilisation dans getTotalDepenses
                window.depensesTotalBackend = data.total;
            } else {
                const errorMessage = data.error || data.message || 'Erreur lors du chargement des dépenses';
                console.error('❌ Erreur lors de la récupération des dépenses client:', errorMessage, data);
                setError(`Erreur lors de la récupération des dépenses client: ${errorMessage}`);
            }
        } catch (err) {
            console.error('💥 Erreur catch lors de la récupération des dépenses:', err);
            setError(`Erreur lors de la récupération des dépenses bureau: ${err.message || 'Erreur réseau'}`);
        }
    };

    const fetchBeneficiaireData = async (beneficiaire) => {
        try {
            const response = await apiFetch(`/api/statistics/etat-beneficiaire?beneficiaire=${encodeURIComponent(beneficiaire)}`);
            const data = await response.json();

            if (data.success) {
                setBeneficiaireData(data.data);
                setFilterInfo({
                    type: 'beneficiaire',
                    value: beneficiaire,
                    beneficiaireName: beneficiaire
                });
            } else {
                setError(data.error || 'Erreur lors du chargement des données du bénéficiaire');
            }
        } catch (err) {
            setError('Erreur lors du chargement des données du bénéficiaire');
        }
    };

    const formatMontant = (montant) => {
        // Si montant est null ou undefined, retourner 0,000
        if (montant === null || montant === undefined) return '0,000 TND';

        // Convertir en nombre si nécessaire
        let montantNum = typeof montant === 'number' ? montant : parseFloat(String(montant).replace(/,/g, '.').replace(/\s/g, '')) || 0;

        // Gérer les nombres négatifs
        const isNegative = montantNum < 0;
        montantNum = Math.abs(montantNum);

        // Tronquer au lieu d'arrondir pour préserver les valeurs exactes
        // Multiplier par 1000, tronquer avec Math.floor, puis diviser par 1000
        // Utiliser Math.floor pour éviter les erreurs d'arrondissement de JavaScript
        const montantTronque = Math.floor(montantNum * 1000 + 0.0000001) / 1000; // +0.0000001 pour gérer les erreurs de précision flottante

        // Convertir en chaîne avec toFixed pour avoir une représentation précise
        // puis extraire manuellement les 3 premières décimales pour éviter l'arrondissement
        const tempStr = montantTronque.toFixed(10); // Utiliser plus de décimales pour éviter les erreurs
        const parts = tempStr.split('.');
        let integerPart = parts[0] || '0';
        let decimalPart = parts[1] ? parts[1].substring(0, 3) : '000'; // Tronquer à 3 décimales

        // S'assurer que la partie décimale a exactement 3 chiffres
        if (decimalPart.length < 3) {
            decimalPart = decimalPart.padEnd(3, '0');
        }

        // Formater la partie entière avec des espaces comme séparateurs de milliers
        const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

        // Utiliser le format français avec virgule comme séparateur décimal
        return `${isNegative ? '-' : ''}${integerFormatted},${decimalPart} TND`;
    };

    const renderMontant = (montant) => {
        const full = typeof montant === 'string' && montant.includes('TND')
            ? montant
            : formatMontant(montant);
        const num = String(full).replace(/\s*TND\s*$/i, '');
        return (
            <>
                {num}
                <span className="etat-tnd"> TND</span>
            </>
        );
    };

    const renderEtatDate = (dateStr) => {
        const full = formatDateForDisplay(dateStr);
        if (typeof full !== 'string' || !/^\d{2}\/\d{2}\/\d{4}$/.test(full)) return full;
        return (
            <>
                {full.slice(0, 5)}
                <span className="etat-date-year">{full.slice(5)}</span>
            </>
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const parts = dateStr.slice(0, 10).split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        const date = new Date(dateStr);
        if (isNaN(date)) return String(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}/${month}/${year}`;
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '-';

        // Si c'est un filtre de période, afficher la période complète
        if (filterInfo && filterInfo.type === 'jour' && filterInfo.subFilterType === 'periode') {
            return filterInfo.value; // Afficher "2025-10-01 - 2025-10-04"
        } else if (filterInfo && filterInfo.type === 'periode') {
            return filterInfo.value; // Afficher "2025-10-01 - 2025-10-04"
        } else {
            // Pour les autres cas, afficher la date normale
            return formatDate(dateStr);
        }
    };

    // Déduire un nom de bénéficiaire pertinent pour l'en-tête des reçus
    const getBeneficiaireName = () => {
        if (filterInfo && filterInfo.beneficiaireName) return filterInfo.beneficiaireName;
        try {
            const uniques = Array.from(new Set((depenses || []).map(d => d && d.beneficiaire).filter(Boolean)));
            if (uniques.length === 1) return uniques[0];
        } catch (_) { /* ignore */ }
        return '';
    };

    const getHonoraireAmount = (honoraire) => {
        // Utiliser le max entre avance et montant comme dans l'impression/backend
        const avanceValue = parseFloat(honoraire.avance || 0);
        const montantValue = parseFloat(honoraire.montant || 0);
        return Math.max(avanceValue, montantValue);
    };

    // Séparer les honoraires reçus des avances
    const getHonorairesRecus = () => {
        return honoraires.filter(honoraire => {
            const libelle = (honoraire.libelle || '').toUpperCase();
            const isHonBase = isHonoraireRecu(honoraire.libelle);
            const hasAmount = parseFloat(honoraire.avance || honoraire.montant || 0) > 0;
            return isHonBase && hasAmount;
        });
    };

    const getAvances = () => {
        return honoraires.filter(honoraire => {
            const libelle = (honoraire.libelle || '').toUpperCase();
            const isAvance = libelle.includes('AVANCE') && !libelle.includes('HONORAIRES REÇU') && !libelle.includes('HONORAIRES RECU');
            const hasAmount = parseFloat(honoraire.avance || honoraire.montant || 0) > 0;
            return isAvance && hasAmount;
        });
    };

    const getTotalHonorairesRecus = () => {
        const etatType = window.currentEtatType;
        if (etatType === 'bureau') {
            // Pour l'État Bureau : somme de tous les honoraires reçus (max entre avance/montant)
            return honoraires.reduce((total, honoraire) => total + getHonoraireAmount(honoraire), 0);
        } else {
            // Pour l'État Client : somme uniquement des honoraires reçus
            return getHonorairesRecus().reduce((total, honoraire) => {
                return total + getHonoraireAmount(honoraire);
            }, 0);
        }
    };

    const getTotalAvances = () => {
        const etatType = window.currentEtatType;
        if (etatType === 'bureau') {
            // Pour l'État Bureau : pas d'avances séparées
            return 0;
        } else {
            // Pour l'État Client : somme des avances
            return getAvances().reduce((total, honoraire) => {
                return total + parseFloat(honoraire.avance || 0);
            }, 0);
        }
    };

    const getTotalMontant = () => {
        // Si c'est un état par bénéficiaire, utiliser les données du bénéficiaire
        if (beneficiaireData) {
            return beneficiaireData.honoraires.total || 0;
        }

        const etatType = window.currentEtatType;

        if (etatType === 'bureau') {
            // Pour l'État Bureau : somme de tous les honoraires reçus (max entre avance/montant)
            return honoraires.reduce((total, honoraire) => total + getHonoraireAmount(honoraire), 0);
        } else if (etatType === 'fond-client-cgm') {
            // Pour fond client chez CGM : somme uniquement des charges avec libellé "ARGENT CLIENT CHEZ CGM"
            return honoraires.reduce((total, honoraire) => total + getHonoraireAmount(honoraire), 0);
        } else {
            // Pour l'État Client : somme des honoraires reçus et des avances
            return getTotalHonorairesRecus() + getTotalAvances();
        }
    };

    const getTotalDepenses = () => {
        // Pour fond client chez CGM, ne pas afficher les dépenses
        const etatType = window.currentEtatType || type;
        if (etatType === 'fond-client-cgm') {
            return 0;
        }

        // Si c'est un état par bénéficiaire, utiliser les données du bénéficiaire
        if (beneficiaireData) {
            return beneficiaireData.depenses.total || 0;
        }

        // Filtrer les dépenses selon le type d'état (même logique que l'affichage)
        const filteredDepenses = depenses.filter(depense => {
            // Exclure les dépenses avec carte bancaire pour tous les types d'états
            const libelleText = (depense.description || depense.libelle || '').toUpperCase();
            const isCarteBancaire = libelleText.includes('[CARTE BANCAIRE]') || libelleText.includes('[CARTE]');
            if (isCarteBancaire) {
                return false;
            }

            if (etatType === 'bureau') {
                // Bureau: inclure toutes les dépenses bureau (les libellés ne portent plus [CGM])
                return true;
            } else if (etatType === 'client') {
                // Client: inclure uniquement les dépenses qui proviennent de charges (charge_id existe)
                // Cela correspond au tableau des charges qui n'affiche que les charges de charges_mensuelles
                // Exclure les dépenses manuelles de depenses_client qui n'ont pas de charge_id
                return depense.charge_id !== null && depense.charge_id !== undefined;
            }
            return true;
        });

        // Utiliser les mêmes dépenses qui sont affichées dans le tableau
        // Les montants arrivent maintenant comme des nombres (DECIMAL) depuis le backend
        let total = 0;
        filteredDepenses.forEach(depense => {
            // Les montants sont maintenant des nombres, pas besoin de conversion complexe
            const montantValue = typeof depense.montant === 'number'
                ? depense.montant
                : parseFloat(depense.montant || 0) || 0;
            total += montantValue;
        });

        return total;
    };

    const getTotalPayeParFondClient = () => {
        const etatType = window.currentEtatType || type;
        if (etatType !== 'fond-client-cgm') {
            return 0;
        }
        return chargesPayeesParFondClient.reduce((total, charge) => {
            return total + parseFloat(charge.montant || 0);
        }, 0);
    };

    const getResteCaisseCgmFinal = () => {
        const etatType = window.currentEtatType || type;
        const currentFilterType = filterInfo?.type || filterType;

        if (etatType !== 'bureau') {
            return 0;
        }

        // Pour le filtre par jour, calculer : solde début journée + honoraires du jour - dépenses du jour
        if (currentFilterType === 'jour') {
            return getCaisseCarryAmount() + getTotalMontant() - getTotalDepenses();
        }

        // Pour les autres filtres, retourner le solde actuel
        return soldeCaisseCgm;
    };

    const getResteCgm = () => {
        // Si c'est un état par bénéficiaire, utiliser le solde calculé
        if (beneficiaireData) {
            return beneficiaireData.solde || 0;
        }

        const etatType = window.currentEtatType || type;
        const currentFilterType = filterInfo?.type || filterType;
        const isFilterByDay = currentFilterType === 'jour';
        const isFilterByMonth = currentFilterType === 'mois';
        const isFilterByPeriod = currentFilterType === 'periode';
        const isFilterByYear = currentFilterType === 'annee';

        if (etatType === 'bureau') {
            // Pour l'État Bureau filtré par jour, mois, période ou année :
            // utiliser honoraires + caisse de la veille (ou fin de période précédente) - dépenses
            if (isFilterByDay || isFilterByMonth || isFilterByPeriod || isFilterByYear) {
                return getCaisseCarryAmount() + getTotalMontant() - getTotalDepenses();
            }
            // Pour les autres filtres : utiliser seulement les honoraires reçus
            return getTotalMontant() - getTotalDepenses();
        } else if (etatType === 'fond-client-cgm') {
            // Pour fond client chez CGM : Reste = Total des fonds - Total payé par fond client
            return getTotalMontant() - getTotalPayeParFondClient();
        } else {
            // Pour l'État Client : utiliser les AVANCES + HONORAIRES REÇUS dans le calcul
            return (getTotalAvances() + getTotalHonorairesRecus()) - getTotalDepenses();
        }
    };

    const getResteCgmFinal = () => {
        const resteCgm = getResteCgm();
        const soldeReporteValue = soldeReporte ? soldeReporte.soldeReporte : 0;
        return resteCgm + soldeReporteValue;
    };

    const getHonorairesLabel = () => {
        if (beneficiaireData) {
            return 'Total Honoraires Reçus (Tous)';
        }

        const etatType = window.currentEtatType;
        if (etatType === 'bureau') {
            return 'Total Honoraires Reçus (Tous)';
        } else {
            return 'Total Honoraires Reçus';
        }
    };

    const getDepensesLabel = () => {
        if (beneficiaireData) {
            return `Dépenses - ${beneficiaireData.beneficiaire}`;
        }

        const etatType = window.currentEtatType;
        if (etatType === 'bureau') {
            return 'Total Dépenses Bureau';
        } else {
            return 'Total Dépenses';
        }
    };

    const getResteLabel = () => {
        if (beneficiaireData) {
            return `Solde (Honoraires - Dépenses ${beneficiaireData.beneficiaire})`;
        }

        const etatType = window.currentEtatType;
        if (etatType === 'bureau') {
            return 'État Bureau';
        } else {
            return 'Reste CGM';
        }
    };

    const getCurrentDate = () => {
        // Si c'est un filtre de période, afficher la période
        if (filterInfo && (
            (filterInfo.type === 'jour' && filterInfo.subFilterType === 'periode') ||
            filterInfo.type === 'periode'
        )) {
            return filterInfo.value; // Afficher "2025-10-01 - 2025-10-04"
        } else if (filterInfo && filterInfo.type === 'mois') {
            return filterInfo.value; // Afficher "10/2025"
        } else if (filterInfo && filterInfo.type === 'annee') {
            return filterInfo.value; // Afficher "2025"
        } else {
            // Par défaut, afficher la date actuelle
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            return `${day}/${month}/${year}`;
        }
    };

    const getPreviousClosingDateForDisplay = () => {
        const currentFilterType = filterInfo?.type || filterType;
        const rawDate = date || filterInfo?.value;

        if (currentFilterType === 'jour' && rawDate) {
            const [year, month, day] = String(rawDate).slice(0, 10).split('-').map(Number);
            if (year && month && day) {
                const previous = new Date(year, month - 1, day);
                previous.setDate(previous.getDate() - 1);
                const y = previous.getFullYear();
                const m = String(previous.getMonth() + 1).padStart(2, '0');
                const d = String(previous.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            }
        }

        if (currentFilterType === 'mois') {
            const currentMois = mois || filterInfo?.mois;
            const currentAnnee = annee || filterInfo?.annee;
            if (currentMois && currentAnnee) {
                const year = parseInt(currentAnnee, 10);
                const month = parseInt(currentMois, 10);
                const previousMonth = month === 1 ? 12 : month - 1;
                const previousYear = month === 1 ? year - 1 : year;
                const lastDayPrevMonth = new Date(previousYear, previousMonth, 0).getDate();
                return `${previousYear}-${String(previousMonth).padStart(2, '0')}-${String(lastDayPrevMonth).padStart(2, '0')}`;
            }
        }

        if (currentFilterType === 'annee') {
            const currentAnnee = annee || filterInfo?.annee || filterInfo?.value;
            if (currentAnnee) {
                const prevYear = parseInt(currentAnnee, 10) - 1;
                return `${prevYear}-12-31`;
            }
        }

        if (currentFilterType === 'periode') {
            const startDate = dateDebut || filterInfo?.dateDebut || (typeof filterInfo?.value === 'string' && filterInfo.value.includes(' - ') ? filterInfo.value.split(' - ')[0] : filterInfo?.value);
            if (startDate) {
                const [year, month, day] = String(startDate).slice(0, 10).split('-').map(Number);
                if (year && month && day) {
                    const previous = new Date(year, month - 1, day);
                    previous.setDate(previous.getDate() - 1);
                    const y = previous.getFullYear();
                    const m = String(previous.getMonth() + 1).padStart(2, '0');
                    const d = String(previous.getDate()).padStart(2, '0');
                    return `${y}-${m}-${d}`;
                }
            }
        }

        return rawDate || null;
    };

    const getPeriodStartDateForCarry = () => {
        const currentFilterType = filterInfo?.type || filterType;
        if (currentFilterType === 'jour') {
            return String(date || filterInfo?.value || '').slice(0, 10) || null;
        }
        if (currentFilterType === 'mois') {
            const currentMois = mois || filterInfo?.mois;
            const currentAnnee = annee || filterInfo?.annee;
            if (currentMois && currentAnnee) {
                return `${currentAnnee}-${String(currentMois).padStart(2, '0')}-01`;
            }
        }
        if (currentFilterType === 'annee') {
            const currentAnnee = annee || filterInfo?.annee || filterInfo?.value;
            if (currentAnnee) return `${currentAnnee}-01-01`;
        }
        if (currentFilterType === 'periode') {
            const startDate = dateDebut || filterInfo?.dateDebut || (typeof filterInfo?.value === 'string' && filterInfo.value.includes(' - ') ? filterInfo.value.split(' - ')[0] : filterInfo?.value);
            return startDate ? String(startDate).slice(0, 10) : null;
        }
        return null;
    };

    const getCaisseCarryAmount = () => {
        const prev = getPreviousClosingDateForDisplay();
        const periodStart = getPeriodStartDateForCarry();
        const currentFilterType = filterInfo?.type || filterType;
        return shouldShowCaisseCarryLine(prev, periodStart, currentFilterType) ? (soldeDebutJournee || 0) : 0;
    };

    const etatTypeForFondFilter = window.currentEtatType || type;
    const isFondClientCgmForFilter = etatTypeForFondFilter === 'fond-client-cgm';
    const fondClientSearchNorm = fondClientSearch.trim().toLowerCase();
    const rowMatchesFondClientSearch = (nom, prenom) => {
        if (!fondClientSearchNorm) return true;
        const full = `${nom || ''} ${prenom || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');
        return full.includes(fondClientSearchNorm);
    };
    const honorairesFondClientFiltered = isFondClientCgmForFilter
        ? honoraires.filter((h) => rowMatchesFondClientSearch(h.client_nom, h.client_prenom))
        : honoraires;
    const chargesPayeesFondClientFiltered = isFondClientCgmForFilter
        ? chargesPayeesParFondClient.filter((c) => rowMatchesFondClientSearch(c.client_nom, c.client_prenom))
        : chargesPayeesParFondClient;
    const getTotalMontantFondFiltered = () =>
        honorairesFondClientFiltered.reduce((t, h) => t + getHonoraireAmount(h), 0);
    const getTotalPayeParFondClientFiltered = () =>
        chargesPayeesFondClientFiltered.reduce((t, c) => t + parseFloat(c.montant || 0), 0);
    const getResteCgmFondFiltered = () =>
        getTotalMontantFondFiltered() - getTotalPayeParFondClientFiltered();
    const getResteCgmFinalFondFiltered = () =>
        getResteCgmFondFiltered() + (soldeReporte ? parseFloat(soldeReporte.soldeReporte || 0) : 0);

    const recordPrintHistory = async () => {
        try {
            // Récupérer le nom d'utilisateur du caissier
            let caissierUsername = 'Caissier Inconnu';
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user && user.username) {
                    caissierUsername = user.username;
                }
            } catch (e) {
                console.warn('Erreur lors de la récupération du nom d\'utilisateur:', e);
            }

            // Déterminer le type de reçu et les données à enregistrer
            let printData = {
                type_reçu: 'honoraires',
                montant: getTotalMontant(),
                caissier_username: caissierUsername
            };

            // Si c'est un état par client
            if (filterInfo && filterInfo.clientName) {
                const clientNameParts = filterInfo.clientName.split(' ');
                printData.client_nom = clientNameParts[clientNameParts.length - 1] || '';
                printData.client_prenom = clientNameParts.slice(0, -1).join(' ') || '';
                printData.type_reçu = 'etat_client';

                // Récupérer l'ID du client depuis l'URL ou les données
                const clientId = window.currentEtatClientId || window.currentHonorairesClientId;
                if (clientId) {
                    printData.client_id = clientId;
                }
            }
            // Si c'est un état par bénéficiaire
            else if (filterInfo && filterInfo.beneficiaireName) {
                printData.type_reçu = 'etat_beneficiaire';
                // Pour les bénéficiaires, on n'a pas d'ID client spécifique
                printData.client_id = null;
                printData.client_nom = 'Bénéficiaire';
                printData.client_prenom = filterInfo.beneficiaireName;
            }
            // Si c'est un état fond client chez CGM
            else if ((window.currentEtatType || type) === 'fond-client-cgm') {
                printData.client_id = null;
                printData.client_nom = 'Fond';
                printData.client_prenom = 'Client Chez CGM';
                printData.type_reçu = 'fond_client_cgm';
            }
            // Si c'est un état bureau général
            else {
                printData.client_id = null;
                printData.client_nom = 'Bureau';
                printData.client_prenom = 'Général';
                printData.type_reçu = 'etat_cgm';
            }

            // Envoyer les données au backend
            const response = await apiFetch('/api/print-history', {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify(printData)
            });

            if (!response.ok) {
                console.warn('Erreur lors de l\'enregistrement de l\'impression:', response.statusText);
            }
        } catch (error) {
            console.warn('Erreur lors de l\'enregistrement de l\'impression:', error);
        }
    };

    const handlePrint = () => {
        // Enregistrer l'impression avant d'imprimer
        recordPrintHistory();

        // Utiliser les nouvelles routes d'impression dédiées
        const etatType = window.currentEtatType || type; // 'bureau' ou 'client'
        const params = new URLSearchParams();

        if (filterType === 'jour' && date) {
            params.append('filterType', 'jour');
            params.append('date', date);
        } else if (filterType === 'periode' && dateDebut && dateFin) {
            params.append('filterType', 'periode');
            params.append('dateDebut', dateDebut);
            params.append('dateFin', dateFin);
        } else if (filterType === 'mois' && mois && annee) {
            params.append('filterType', 'mois');
            params.append('mois', mois);
            params.append('annee', annee);
        } else if (filterType === 'annee' && annee) {
            params.append('filterType', 'annee');
            params.append('annee', annee);
        }

        if (etatType === 'client') {
            const clientId = window.currentEtatClientId || window.currentHonorairesClientId;
            if (clientId) params.append('client_id', clientId);
        }

        // Pour fond client chez CGM, utiliser la route etat-cgm avec un paramètre spécial
        if (etatType === 'fond-client-cgm') {
            params.append('type', 'fond-client-cgm');
        }

        const base = (etatType === 'bureau' || etatType === 'fond-client-cgm') ? '/etat-cgm/print' : '/etat-client/print';
        const url = `${base}?${params.toString()}`;
        window.open(url, '_blank');
    };

    if (!show) return null;

    return (
        <>
            <div className="modal fade show etat-inventory-modal" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog modal-xl modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {(() => {
                                    const etatType = window.currentEtatType || type;
                                    if (etatType === 'fond-client-cgm') {
                                        return fondClientView === 'mouvements' ? 'Fond client' : 'Fond Client Chez CGM';
                                    } else if (filterInfo && filterInfo.beneficiaireName) {
                                        return `État Bénéficiaire - ${filterInfo.beneficiaireName}`;
                                    } else if (filterInfo && filterInfo.clientName) {
                                        return `État client · ${filterInfo.clientName}`;
                                    } else if (etatType === 'bureau') {
                                        return 'État Bureau';
                                    } else if (etatType === 'client') {
                                        return clientName ? `État client · ${clientName}` : 'État client';
                                    } else {
                                        return 'États CGM - Honoraires Reçus';
                                    }
                                })()}
                                {filterInfo && (
                                    <small className="ms-2">
                                        {filterInfo.type === 'jour' ?
                                            (filterInfo.subFilterType === 'date' ? `Jour: ${filterInfo.value}` : `Période: ${filterInfo.value}`) :
                                            filterInfo.type === 'periode' ? `Période: ${filterInfo.value}` :
                                                filterInfo.type === 'mois' ? `Mois: ${filterInfo.value}` :
                                                    filterInfo.type === 'annee' ? `Année: ${filterInfo.value}` :
                                                        filterInfo.value
                                        }
                                    </small>
                                )}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>
                        <div className="modal-body">
                            {loading && (
                                <div className="text-center py-4">
                                    <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Chargement de l&apos;état…</p>
                                </div>
                            )}

                            {error && (
                                <div className="alert alert-danger">
                                    {error}
                                </div>
                            )}

                            {!loading && !error && (
                                <div className="etat-inventory">
                                    {(window.currentEtatType || type) === 'fond-client-cgm' && (
                                        <div className="mb-1">
                                            <label htmlFor="fond-client-search" className="form-label small text-muted mb-1">
                                                Rechercher par nom de client
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light"><i className="fas fa-search" aria-hidden /></span>
                                                <input
                                                    id="fond-client-search"
                                                    type="search"
                                                    className="form-control"
                                                    placeholder="Nom, prénom ou partie du nom affiché dans la colonne Client…"
                                                    value={fondClientSearch}
                                                    onChange={(e) => setFondClientSearch(e.target.value)}
                                                    autoComplete="off"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {(() => {
                                        const etatType = window.currentEtatType || type;
                                        const currentFilterType = filterInfo?.type || filterType;
                                        
                                        // Pour le mode bureau avec filtre jour/mois/période/année, on affiche TOUJOURS le solde d'ouverture
                                        // même si aucune opération n'existe ce jour-là
                                        const isBureau = etatType === 'bureau';
                                        const hasDateFilter = currentFilterType === 'jour' || currentFilterType === 'mois' 
                                                          || currentFilterType === 'periode' || currentFilterType === 'annee';
                                        const hasCaisseOnly = isBureau && hasDateFilter;
                                        
                                        const noHonorairesEtDepenses = honoraires.length === 0 && depenses.length === 0 && !beneficiaireData;

                                        // Afficher "Aucune donnée" seulement si pas d'opérations ET pas de filtre date pour afficher le solde d'ouverture
                                        if (noHonorairesEtDepenses && !hasCaisseOnly) {
                                            return (
                                                <div className="text-center py-4">
                                                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                                    <h5 className="text-muted">Aucune donnée trouvée</h5>
                                                    <p className="text-muted">Aucun honoraire ou dépense n&apos;a été enregistré.</p>
                                                </div>
                                            );
                                        }

                                        return (
                                            <>

                                                {/* Tableau des Honoraires et Avances (unifié) */}
                                                {(() => {
                                                    const etatType = window.currentEtatType || type;
                                                    const isClientMode = etatType === 'client';
                                                    const honorairesRecus = getHonorairesRecus();
                                                    const avances = getAvances();

                                                    // Combiner les honoraires reçus et les avances pour l'état client
                                                    let allHonoraires = [];
                                                    if (isClientMode) {
                                                        // Pour l'état client : combiner honoraires reçus et avances
                                                        allHonoraires = [
                                                            ...honorairesRecus.map(h => ({ ...h, type: 'honoraire' })),
                                                            ...avances.map(h => ({ ...h, type: 'avance' }))
                                                        ].sort((a, b) => new Date(a.date) - new Date(b.date));
                                                    } else if (etatType === 'fond-client-cgm') {
                                                        // Pour fond client chez CGM : seulement les charges avec libellé "ARGENT CLIENT CHEZ CGM" (filtrées par recherche client)
                                                        allHonoraires = honorairesFondClientFiltered.map(h => ({ ...h, type: 'fond-client' }));
                                                    } else {
                                                        // Pour l'état bureau : seulement les honoraires
                                                        allHonoraires = honoraires
                                                            .map(h => ({ ...h, type: 'honoraire' }))
                                                            .sort((a, b) => new Date(a.date) - new Date(b.date));
                                                    }

                                                    // Toujours afficher le tableau pour l'état client, même s'il est vide
                                                    // Pour fond client chez CGM, afficher uniquement les charges avec le libellé "ARGENT CLIENT CHEZ CGM"
                                                    const isFondClientCgm = etatType === 'fond-client-cgm';
                                                    if (beneficiaireData || allHonoraires.length > 0 || etatType === 'bureau' || (isClientMode && honoraires.length > 0) || isFondClientCgm) {
                                                        const totalHonoLabel = isFondClientCgm
                                                            ? formatMontant(getTotalMontantFondFiltered())
                                                            : (() => {
                                                                const currentFilterType = filterInfo?.type || filterType;
                                                                const isFilterByDay = currentFilterType === 'jour';
                                                                const isFilterByMonth = currentFilterType === 'mois';
                                                                const isFilterByPeriod = currentFilterType === 'periode';
                                                                const isFilterByYear = currentFilterType === 'annee';
                                                                if (etatType === 'bureau' && (isFilterByDay || isFilterByMonth || isFilterByPeriod || isFilterByYear)) {
                                                                    return formatMontant(getCaisseCarryAmount() + getTotalHonorairesRecus() + getTotalAvances());
                                                                }
                                                                return formatMontant(getTotalHonorairesRecus() + getTotalAvances());
                                                            })();
                                                        return (
                                                            <div className="nh-modern-table-card nh-modern-table-card--honoraires">
                                                                <div className="nh-modern-table-toolbar">
                                                                    <h6 className="nh-modern-table-toolbar__title">
                                                                        {isFondClientCgm ? 'Détail des Fonds Clients chez CGM' : (etatType === 'bureau' ? 'Détail des Honoraires, Avances et Caisse' : 'Détail des Honoraires et Avances')}
                                                                    </h6>
                                                                    <div className="nh-modern-table-info">
                                                                        Total <strong className="etat-amount--credit">{renderMontant(totalHonoLabel)}</strong>
                                                                    </div>
                                                                </div>
                                                                <div className="overview-table-container nh-modern-table-scroll">
                                                                    <table className="table overview-table nh-overview-data-table nh-modern-data-table etat-modal-table">
                                                                    <thead>
    <tr>
        <th><span className="nh-modern-th-inner">Date</span></th>
        <th><span className="nh-modern-th-inner">Libellé</span></th>
        {!isClientMode && <th className="etat-col-client"><span className="nh-modern-th-inner">Client</span></th>}
        {!isFondClientCgm && <th><span className="nh-modern-th-inner">Montant</span></th>}
    </tr>
</thead>
                                                                        <tbody>
                                                                            {/* Ligne "Montant Caisse" pour l'état bureau filtré par jour, par mois, par période ou par année */}
                                                                            {(() => {
                                                                                const etatType = window.currentEtatType || type;
                                                                                const isBureau = etatType === 'bureau';
                                                                                const currentFilterType = filterInfo?.type || filterType;
                                                                                const isFilterByDay = currentFilterType === 'jour';
                                                                                const isFilterByMonth = currentFilterType === 'mois';
                                                                                const isFilterByYear = currentFilterType === 'annee';
                                                                                const isFilterByPeriod = currentFilterType === 'periode';

                                                                                // Le solde d'ouverture affiché est toujours le solde de clôture de la veille
                                                                                // (ou de la fin de la période précédente). La requête backend conserve la date
                                                                                // courante pour calculer le solde avant le début de la journée sélectionnée.
                                                                                const selectedDateForDisplay = getPreviousClosingDateForDisplay();
                                                                                const periodStart = getPeriodStartDateForCarry();

                                                                                if (isBureau && (isFilterByDay || isFilterByMonth || isFilterByPeriod || isFilterByYear) && selectedDateForDisplay && shouldShowCaisseCarryLine(selectedDateForDisplay, periodStart, currentFilterType)) {
                                                                                    const montantCaisse = getCaisseCarryAmount();
                                                                                    return (
                                                                                        <tr>
                                                                                            <td className="etat-date">{renderEtatDate(selectedDateForDisplay)}</td>
                                                                                            <td className="etat-libelle">
                                                                                                <strong>
                                                                                                    Montant Caisse ({formatDateForDisplay(selectedDateForDisplay)})
                                                                                                </strong>
                                                                                            </td>
                                                                                            {!isClientMode && (
                                                                                                <td className="text-muted etat-col-client">
                                                                                                    CGM
                                                                                                </td>
                                                                                            )}
                                                                                            <td className={`text-end ${montantCaisse >= 0 ? 'etat-amount--credit' : 'etat-amount--danger'}`}>
                                                                                                {renderMontant(montantCaisse)}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                            {beneficiaireData ? (
                                                                                <tr>
                                                                                    <td colSpan={isClientMode ? "2" : "3"} className="text-center">
                                                                                        <strong>Total des honoraires reçus</strong>
                                                                                    </td>
                                                                                    <td className="text-end etat-amount--credit">
                                                                                        {formatMontant(beneficiaireData.honoraires.total)}
                                                                                    </td>
                                                                                </tr>
                                                                            ) : allHonoraires.length === 0 ? (
                                                                                <tr>
                                                                                    <td colSpan={isClientMode ? "3" : "4"} className="etat-empty">
                                                                                        Aucun honoraire reçu et aucune avance
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                allHonoraires.map((honoraire, index) => {
                                                                                    const isAvance = honoraire.type === 'avance';
                                                                                    const montant = isAvance
                                                                                        ? parseFloat(honoraire.avance || 0)
                                                                                        : getHonoraireAmount(honoraire);
                                                                                    return (
                                                                                        <tr key={index}>
                                                                                            <td className="etat-date">{renderEtatDate(honoraire.date)}</td>
                                                                                            <td className="etat-libelle">
                                                                                                {formatLibelleForDisplay(honoraire.libelle)}
                                                                                            </td>
                                                                                            {!isClientMode && (
                                                                                                <td className="text-muted etat-col-client">
                                                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                                                        <span>
                                                                                                            {honoraire.client_nom || ''} {honoraire.client_prenom || ''}
                                                                                                        </span>
                                                                                                        {isFondClientCgm && (
                                                                                                            <button
                                                                                                                type="button"
                                                                                                                className="btn btn-sm btn-outline-primary ms-2"
                                                                                                                onClick={() =>
                                                                                                                    setFondClientDetails({
                                                                                                                        clientId: honoraire.client_id,
                                                                                                                        clientNom: honoraire.client_nom || '',
                                                                                                                        clientPrenom: honoraire.client_prenom || ''
                                                                                                                    })
                                                                                                                }
                                                                                                            >
                                                                                                                Détails
                                                                                                            </button>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </td>
                                                                                            )}
                                                                                           {!isFondClientCgm && (
    <td className="text-end etat-amount--credit">
        {renderMontant(montant)}
    </td>
)}
                                                                                        </tr>
                                                                                    );
                                                                                })
                                                                            )}
                                                                        </tbody>
                                                                   <tfoot>
    <tr>
        <td colSpan={isFondClientCgm ? (isClientMode ? "2" : "3") : (isClientMode ? "2" : "3")} className="text-end">
            {isFondClientCgm ? 'Total des argent client chez cgm :' : (etatType === 'bureau' ? 'TOTAL DES HONORAIRES ET CAISSE :' : 'TOTAL HONORAIRES ET AVANCES :')}
        </td>
        {!isFondClientCgm && (
            <td className="text-end etat-amount--credit">
                {renderMontant(totalHonoLabel)}
            </td>
        )}
    </tr>
</tfoot>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* Tableau des Charges Payées par Fond Client (uniquement pour fond client chez CGM) */}
                                                {(() => {
                                                    const etatType = window.currentEtatType || type;
                                                    const isFondClientCgm = etatType === 'fond-client-cgm';

                                                    if (isFondClientCgm && chargesPayeesParFondClient.length > 0) {
                                                        return (
                                                            <div className="nh-modern-table-card">
                                                                <div className="nh-modern-table-toolbar">
                                                                    <h6 className="nh-modern-table-toolbar__title">Payé par client</h6>
                                                                    <div className="nh-modern-table-info">
                                                                        Total <strong className="etat-amount--danger">{formatMontant(getTotalPayeParFondClientFiltered())}</strong>
                                                                    </div>
                                                                </div>
                                                                <div className="overview-table-container nh-modern-table-scroll">
                                                                    <table className="table overview-table nh-overview-data-table nh-modern-data-table etat-modal-table">
                                                                     <thead>
    <tr>
        <th><span className="nh-modern-th-inner">Date</span></th>
        <th><span className="nh-modern-th-inner">Libellé</span></th>
        <th><span className="nh-modern-th-inner">Client</span></th>
    </tr>
</thead>
                                                                 <tbody>
    {chargesPayeesFondClientFiltered.map((charge, index) => (
        <tr key={index}>
            <td className="etat-date">{renderEtatDate(charge.date)}</td>
            <td className="etat-libelle">
                {formatLibelleForDisplay(charge.libelle)}
            </td>
            <td className="text-muted">
                <div className="d-flex justify-content-between align-items-center">
                    <span>
                        {charge.client_nom || ''} {charge.client_prenom || ''}
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={() =>
                            setFondClientDetails({
                                clientId: charge.client_id,
                                clientNom: charge.client_nom || '',
                                clientPrenom: charge.client_prenom || ''
                            })
                        }
                    >
                        Détails
                    </button>
                </div>
            </td>
        </tr>
    ))}
</tbody>
                                                                        <tfoot>
    <tr>
        <td colSpan="3" className="text-end">
            Total payé par fond client :
        </td>
    </tr>
</tfoot>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* Tableau des Dépenses */}
                                                {(depenses.length > 0 || beneficiaireData) && (() => {
                                                    const etatType = window.currentEtatType || type;
                                                    // Ne pas afficher les dépenses pour fond client chez CGM
                                                    if (etatType === 'fond-client-cgm') {
                                                        return null;
                                                    }
                                                    const isClientMode = etatType === 'client';
                                                    return (
                                                        <div className="nh-modern-table-card nh-modern-table-card--depenses">
                                                            <div className="nh-modern-table-toolbar">
                                                                <h6 className="nh-modern-table-toolbar__title">Détail des Dépenses</h6>
                                                                <div className="nh-modern-table-info">
                                                                    Total <strong className="etat-amount--debit">{renderMontant(getTotalDepenses())}</strong>
                                                                </div>
                                                            </div>
                                                            <div className="overview-table-container nh-modern-table-scroll">
                                                                <table className="table overview-table nh-overview-data-table nh-modern-data-table etat-modal-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th><span className="nh-modern-th-inner">Date</span></th>
                                                                            <th><span className="nh-modern-th-inner">Libellé</span></th>
                                                                            {!isClientMode && <th className="etat-col-client"><span className="nh-modern-th-inner">Client</span></th>}
                                                                            <th><span className="nh-modern-th-inner">Montant</span></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {beneficiaireData ? (
                                                                            beneficiaireData.depenses.details
                                                                                .filter(depense => {
                                                                                    // Exclure les dépenses avec carte bancaire
                                                                                    const libelleText = (depense.description || depense.libelle || '').toUpperCase();
                                                                                    return !libelleText.includes('[CARTE BANCAIRE]') && !libelleText.includes('[CARTE]');
                                                                                })
                                                                                .map((depense, index) => {
                                                                                    const rawText = (depense.description || depense.libelle || '').toUpperCase();
                                                                                    const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');

                                                                                    const clientName = isCgmDepense ? (depense.nom_beneficiaire || depense.beneficiaire || depense.client) : (depense.beneficiaire || depense.client);

                                                                                    let libelleText = depense.description || depense.libelle || '-';
                                                                                    // Remplacer [CGM] par [PAYÉ PAR CGM] dans l'affichage
                                                                                    libelleText = libelleText.replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
                                                                                    // Formater le libellé pour afficher "HONORAIRES REÇU CABINET"
                                                                                    libelleText = formatLibelleForDisplay(libelleText);

                                                                                    return (
                                                                                        <tr key={index}>
                                                                                            <td className="etat-date">{renderEtatDate(depense.date)}</td>
                                                                                            <td className="etat-libelle">{libelleText}</td>
                                                                                            {!isClientMode && (
                                                                                                <td className="etat-col-client">{clientName || '-'}</td>
                                                                                            )}
                                                                                            <td className="text-end etat-amount--debit">
                                                                                                {renderMontant(depense.montant)}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })
                                                                        ) : (
                                                                            depenses
                                                                                .filter(depense => {
                                                                                    const etatType = window.currentEtatType || type;

                                                                                    // Exclure les dépenses avec carte bancaire pour tous les types d'états
                                                                                    const libelleText = (depense.description || depense.libelle || '').toUpperCase();
                                                                                    const isCarteBancaire = libelleText.includes('[CARTE BANCAIRE]') || libelleText.includes('[CARTE]');
                                                                                    if (isCarteBancaire) {
                                                                                        return false;
                                                                                    }

                                                                                    if (etatType === 'bureau') {
                                                                                        return true; // inclure toutes les dépenses bureau
                                                                                    } else if (etatType === 'client') {
                                                                                        // Client: inclure uniquement les dépenses qui proviennent de charges (charge_id existe)
                                                                                        // Cela correspond au tableau des charges qui n'affiche que les charges de charges_mensuelles
                                                                                        // Exclure les dépenses manuelles de depenses_client qui n'ont pas de charge_id
                                                                                        return depense.charge_id !== null && depense.charge_id !== undefined;
                                                                                    }
                                                                                    return true;
                                                                                })
                                                                                .map((depense, index) => {
                                                                                    const rawText = (depense.description || depense.libelle || '').toUpperCase();
                                                                                    const isCgmDepense = rawText.includes('[CGM]') || rawText.includes('HONORAIRES REÇU');

                                                                                    const clientName = isCgmDepense ? (depense.nom_beneficiaire || depense.beneficiaire || depense.client) : (depense.beneficiaire || depense.client);

                                                                                    let libelleText = depense.description || depense.libelle || '-';
                                                                                    // Remplacer [CGM] par [PAYÉ PAR CGM] dans l'affichage
                                                                                    libelleText = libelleText.replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
                                                                                    // Formater le libellé pour afficher "HONORAIRES REÇU CABINET"
                                                                                    libelleText = formatLibelleForDisplay(libelleText);

                                                                                    return (
                                                                                        <tr key={depense.id != null ? depense.id : index}>
                                                                                            <td className="etat-date">{renderEtatDate(depense.date)}</td>
                                                                                            <td className="etat-libelle">{libelleText}</td>
                                                                                            {!isClientMode && (
                                                                                                <td className="etat-col-client">{clientName || '-'}</td>
                                                                                            )}
                                                                                            <td className="text-end etat-amount--debit">
                                                                                                {renderMontant(depense.montant)}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })
                                                                        )}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr>
                                                                            <td colSpan={isClientMode ? 2 : 3} className="text-end">
                                                                                TOTAL DÉPENSES :
                                                                            </td>
                                                                            <td className="text-end etat-amount--debit">
                                                                                {renderMontant(getTotalDepenses())}
                                                                            </td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Synthèse inventaire (cartes) */}
                                                {!(type === 'fond-client-cgm' && fondClientView === 'mouvements') && (() => {
                                                    const etatType = window.currentEtatType || type;
                                                    const isFondClientCgm = etatType === 'fond-client-cgm';
                                                    const totalHono = (() => {
                                                        const currentFilterType = filterInfo?.type || filterType;
                                                        const isFilterByDay = currentFilterType === 'jour';
                                                        const isFilterByMonth = currentFilterType === 'mois';
                                                        const isFilterByPeriod = currentFilterType === 'periode';
                                                        const isFilterByYear = currentFilterType === 'annee';
                                                        if (etatType === 'bureau' && (isFilterByDay || isFilterByMonth || isFilterByPeriod || isFilterByYear)) {
                                                            return getCaisseCarryAmount() + getTotalMontant();
                                                        }
                                                        if (etatType === 'fond-client-cgm') {
                                                            return getTotalMontantFondFiltered();
                                                        }
                                                        return getTotalMontant();
                                                    })();
                                                    const totalDep = isFondClientCgm
                                                        ? getTotalPayeParFondClientFiltered()
                                                        : getTotalDepenses();
                                                    const reste = isFondClientCgm ? getResteCgmFondFiltered() : getResteCgm();
                                                    const resteFinal = isFondClientCgm ? getResteCgmFinalFondFiltered() : getResteCgmFinal();
                                                    return (
                                                        <div className="nh-modern-table-card">
                                                            <div className="nh-modern-table-toolbar">
                                                                <h6 className="nh-modern-table-toolbar__title">Synthèse</h6>
                                                                <div className="nh-modern-table-info etat-synth-meta">
                                                                    Au {getCurrentDate()}
                                                                </div>
                                                            </div>
                                                            <div className="p-3">
                                                                <div className="etat-synth-cards">
                                                                    <div className="etat-synth-card">
                                                                        <div className="etat-synth-card__label">
                                                                            {isFondClientCgm ? 'Fonds clients CGM' : (etatType === 'bureau' ? 'Honoraires + caisse' : 'Honoraires et avances')}
                                                                        </div>
                                                                        <div className="etat-synth-card__value is-credit">
                                                                            {formatMontant(totalHono)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="etat-synth-card">
                                                                        <div className="etat-synth-card__label">
                                                                            {isFondClientCgm ? 'Payé par fond client' : 'Dépenses'}
                                                                        </div>
                                                                        <div className={`etat-synth-card__value ${isFondClientCgm ? 'is-reste-neg' : 'is-debit'}`}>
                                                                            {formatMontant(totalDep)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="etat-synth-card">
                                                                        <div className="etat-synth-card__label">Reste CGM</div>
                                                                        <div className={`etat-synth-card__value ${reste >= 0 ? 'is-reste-pos' : 'is-reste-neg'}`}>
                                                                            {formatMontant(reste)}
                                                                        </div>
                                                                    </div>
                                                                    {soldeReporte && soldeReporte.soldeReporte !== 0 && (
                                                                        <>
                                                                            <div className="etat-synth-card">
                                                                                <div className="etat-synth-card__label">
                                                                                    Solde reporté ({soldeReporte.anneePrecedente})
                                                                                </div>
                                                                                <div className={`etat-synth-card__value ${soldeReporte.soldeReporte >= 0 ? 'is-reste-pos' : 'is-reste-neg'}`}>
                                                                                    {formatMontant(soldeReporte.soldeReporte)}
                                                                                </div>
                                                                            </div>
                                                                            <div className="etat-synth-card">
                                                                                <div className="etat-synth-card__label">Reste CGM final</div>
                                                                                <div className={`etat-synth-card__value ${resteFinal >= 0 ? 'is-reste-pos' : 'is-reste-neg'}`}>
                                                                                    {formatMontant(resteFinal)}
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            {!(type === 'fond-client-cgm' && fondClientView === 'mouvements') && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handlePrint}
                                    title="Imprimer la synthèse"
                                >
                                    <i className="fas fa-print me-2"></i>Imprimer
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de détails Fond Client (par client) */}
            {fondClientDetails && (
                <FondClientDetailsModal
                    show={!!fondClientDetails}
                    onClose={() => setFondClientDetails(null)}
                    clientId={fondClientDetails.clientId}
                    clientNom={fondClientDetails.clientNom}
                    clientPrenom={fondClientDetails.clientPrenom}
                    honoraires={honoraires}
                    chargesPayeesParFondClient={chargesPayeesParFondClient}
                    getCurrentDate={getCurrentDate}
                />
            )}
        </>
    );
}
